import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Clock, Timer, AlertCircle } from 'lucide-react';
import { t } from '@/i18n/ro';
import { useMetadata } from '@/context/MetadataContext';
import {
  getWorkPackage,
  createWorkPackage,
  updateWorkPackage,
  getProjectIdFromLink,
  getProjectWorkPackages,
} from '@/services/workpackages.service';
import { getAccessibleProjects, getProjectVersions, getProjectCategories, getProjectAssignees } from '@/services/projects.service';
import { getWorkPackageTimeEntries, calculateTotalTime } from '@/services/timeentries.service';
import {
  Button,
  Card,
  Input,
  DateInput,
  Select,
  Textarea,
  Tabs,
  TabPanel,
  LoadingState,
} from '@/components/common';
import type { WorkPackage, Project, Version, Category, User, WorkPackageFormData, TimeEntry } from '@/types';
import { extractIdFromLink } from '@/services/api';

// Parsează durata ISO în ore
function parseISODuration(duration?: string | number | null): number {
  if (!duration) return 0;
  if (typeof duration === 'number') return duration;

  const match = String(duration).match(/^PT(?:(\d+)H)?(?:(\d+)M)?$/i);
  if (match) {
    const hours = parseInt(match[1] || '0');
    const minutes = parseInt(match[2] || '0');
    return hours + minutes / 60;
  }
  return parseFloat(String(duration)) || 0;
}

// Convertește ore în format ISO duration
function hoursToISODuration(hours: number): string {
  if (!hours || hours <= 0) return '';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `PT${h}H`;
  return `PT${h}H${m}M`;
}

// Formatează ore pentru afișare
function formatHoursDisplay(hours: number): string {
  if (hours === 0) return '0 ore';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h} ${h === 1 ? 'oră' : 'ore'}`;
  return `${h}h ${m}m`;
}

export function WorkPackageForm() {
  const { workPackageId, projectId: routeProjectId } = useParams<{
    workPackageId?: string;
    projectId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { types, statuses, priorities } = useMetadata();

  const isEditing = !!workPackageId;
  const initialTypeId = searchParams.get('type');
  const initialParentId = searchParams.get('parent');

  const [workPackage, setWorkPackage] = useState<WorkPackage | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [versions, setVersions] = useState<Version[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [assignees, setAssignees] = useState<User[]>([]);
  const [parentWorkPackages, setParentWorkPackages] = useState<WorkPackage[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('essential');

  // Stări separate pentru ore (pentru input mai ușor)
  const [estimatedHours, setEstimatedHours] = useState<number>(0);
  const [remainingHours, setRemainingHours] = useState<number>(0);

  // Form data - pre-fill startDate with current date for new work packages
  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState<WorkPackageFormData>({
    subject: '',
    description: '',
    typeId: initialTypeId ? Number(initialTypeId) : undefined,
    statusId: undefined,
    priorityId: undefined,
    projectId: routeProjectId ? Number(routeProjectId) : undefined,
    assigneeId: undefined,
    responsibleId: undefined,
    categoryId: undefined,
    parentId: initialParentId ? Number(initialParentId) : undefined,
    versionId: undefined,
    startDate: today,
    dueDate: '',
    estimatedTime: '',
    remainingTime: '',
  });

  // Încărcare date
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const projectsData = await getAccessibleProjects();
        setProjects(projectsData);

        if (isEditing && workPackageId) {
          const [wpData, timeEntriesData] = await Promise.all([
            getWorkPackage(Number(workPackageId)),
            getWorkPackageTimeEntries(Number(workPackageId)).catch(() => []),
          ]);
          setWorkPackage(wpData);
          setTimeEntries(timeEntriesData);

          // Populăm form-ul cu datele existente
          const projectId = getProjectIdFromLink(wpData);

          // Parsăm orele din format ISO
          const estHours = parseISODuration(wpData.estimatedTime);
          const remHours = parseISODuration(wpData.remainingTime);
          setEstimatedHours(estHours);
          setRemainingHours(remHours);

          setFormData({
            subject: wpData.subject,
            description: wpData.description?.raw || '',
            typeId: extractIdFromLink(wpData._links.type) || undefined,
            statusId: extractIdFromLink(wpData._links.status) || undefined,
            priorityId: extractIdFromLink(wpData._links.priority) || undefined,
            projectId: projectId || undefined,
            assigneeId: extractIdFromLink(wpData._links.assignee) || undefined,
            responsibleId: extractIdFromLink(wpData._links.responsible) || undefined,
            categoryId: extractIdFromLink(wpData._links.category) || undefined,
            parentId: extractIdFromLink(wpData._links.parent) || undefined,
            versionId: extractIdFromLink(wpData._links.version) || undefined,
            startDate: wpData.startDate || '',
            dueDate: wpData.dueDate || '',
            estimatedTime: wpData.estimatedTime || '',
            remainingTime: wpData.remainingTime || '',
          });

          // Încărcăm datele pentru proiectul selectat
          if (projectId) {
            const [versionsData, categoriesData, assigneesData, wpData] = await Promise.all([
              getProjectVersions(projectId),
              getProjectCategories(projectId),
              getProjectAssignees(projectId),
              getProjectWorkPackages(projectId),
            ]);
            setVersions(versionsData);
            setCategories(categoriesData);
            setAssignees(assigneesData);
            // Excludem activitatea curentă din lista părinților potențiali
            setParentWorkPackages(wpData.filter(wp => wp.id !== Number(workPackageId)));
          }
        } else if (routeProjectId) {
          const [versionsData, categoriesData, assigneesData, wpData] = await Promise.all([
            getProjectVersions(Number(routeProjectId)),
            getProjectCategories(Number(routeProjectId)),
            getProjectAssignees(Number(routeProjectId)),
            getProjectWorkPackages(Number(routeProjectId)),
          ]);
          setVersions(versionsData);
          setCategories(categoriesData);
          setAssignees(assigneesData);
          setParentWorkPackages(wpData);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError(t('errors.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isEditing, workPackageId, routeProjectId]);

  // Încărcare versiuni, categorii, assignees și work packages când se schimbă proiectul
  useEffect(() => {
    const loadProjectData = async () => {
      if (formData.projectId) {
        try {
          const [versionsData, categoriesData, assigneesData, wpData] = await Promise.all([
            getProjectVersions(formData.projectId),
            getProjectCategories(formData.projectId),
            getProjectAssignees(formData.projectId),
            getProjectWorkPackages(formData.projectId),
          ]);
          setVersions(versionsData);
          setCategories(categoriesData);
          setAssignees(assigneesData);
          // Excludem activitatea curentă din lista părinților potențiali
          setParentWorkPackages(wpData.filter(wp => wp.id !== Number(workPackageId)));
        } catch (err) {
          console.error('Failed to load project data:', err);
        }
      } else {
        setVersions([]);
        setCategories([]);
        setAssignees([]);
        setParentWorkPackages([]);
      }
    };

    loadProjectData();
  }, [formData.projectId, workPackageId]);

  const handleChange = (
    field: keyof WorkPackageFormData,
    value: string | number | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Calculează orele completate/înregistrate din time entries
  const spentHours = useMemo(() => {
    return calculateTotalTime(timeEntries).hours;
  }, [timeEntries]);

  // Calculează diferența disponibilă pentru ore rămase
  const availableRemainingHours = useMemo(() => {
    const diff = estimatedHours - spentHours;
    return Math.max(0, diff);
  }, [estimatedHours, spentHours]);

  // Handler pentru schimbarea orelor estimate
  const handleEstimatedHoursChange = (hours: number) => {
    setEstimatedHours(hours);
    setFormData((prev) => ({
      ...prev,
      estimatedTime: hoursToISODuration(hours),
    }));
    // Recalculează orele rămase dacă depășesc noua estimare
    if (remainingHours > hours - spentHours) {
      const newRemaining = Math.max(0, hours - spentHours);
      setRemainingHours(newRemaining);
      setFormData((prev) => ({
        ...prev,
        remainingTime: hoursToISODuration(newRemaining),
      }));
    }
  };

  // Handler pentru schimbarea orelor rămase
  const handleRemainingHoursChange = (hours: number) => {
    // Limitează la diferența disponibilă
    const maxHours = Math.max(0, estimatedHours - spentHours);
    const validHours = Math.min(Math.max(0, hours), maxHours);
    setRemainingHours(validHours);
    setFormData((prev) => ({
      ...prev,
      remainingTime: hoursToISODuration(validHours),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      // Convertim orele în format ISO înainte de salvare
      const dataToSave = {
        ...formData,
        estimatedTime: hoursToISODuration(estimatedHours),
        remainingTime: hoursToISODuration(remainingHours),
      };

      if (isEditing && workPackage) {
        await updateWorkPackage(workPackage.id, workPackage.lockVersion, dataToSave);
      } else if (formData.projectId) {
        await createWorkPackage(formData.projectId, dataToSave);
      }

      navigate(-1);
    } catch (err) {
      console.error('Failed to save work package:', err);
      setError(t('errors.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <LoadingState message={t('common.loading')} />;
  }

  const tabs = [
    { id: 'essential', label: t('workPackages.tabs.essential') },
    { id: 'planning', label: t('workPackages.tabs.planning') },
  ];

  // Filtrăm tipurile - păstrăm doar Activitate și Reper
  const typeOptions = types
    .filter((type) => {
      const name = type.name.toLowerCase();
      return name.includes('activitate') || name.includes('task') ||
             name.includes('reper') || name.includes('milestone') ||
             type.isMilestone;
    })
    .map((type) => ({
      value: type.id,
      label: type.name,
    }));

  const statusOptions = statuses.map((status) => ({
    value: status.id,
    label: status.name,
  }));

  const priorityOptions = priorities.map((priority) => ({
    value: priority.id,
    label: priority.name,
  }));

  const projectOptions = projects.map((project) => ({
    value: project.id,
    label: project.name,
  }));

  const versionOptions = versions.map((version) => ({
    value: version.id,
    label: version.name,
  }));

  const categoryOptions = categories.map((category) => ({
    value: category.id,
    label: category.name,
  }));

  const assigneeOptions = assignees.map((user) => ({
    value: user.id,
    label: (user as any).name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.login,
  }));

  // Opțiuni pentru activitate părinte - excludem activitatea curentă și filtrăm doar faze/activități principale
  const parentOptions = parentWorkPackages
    .filter(wp => {
      // Excludem work package-ul curent (pentru editare)
      if (workPackageId && wp.id === Number(workPackageId)) return false;
      return true;
    })
    .map((wp) => ({
      value: wp.id,
      label: `#${wp.id} - ${wp.subject}`,
    }));

  // Determinăm tipul selectat pentru afișare când este readonly
  const selectedType = types.find(t => t.id === formData.typeId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          {t('common.back')}
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing
            ? t('workPackages.editWorkPackage')
            : t('workPackages.addWorkPackage')}
        </h1>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card padding="none">
          <div className="px-4 pt-4">
            <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
          </div>

          <div className="p-6">
            {/* Tab: Esențial */}
            <TabPanel tabId="essential" activeTab={activeTab}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Input
                    label={t('workPackages.fields.subject')}
                    value={formData.subject}
                    onChange={(e) => handleChange('subject', e.target.value)}
                    required
                    placeholder="Introduceți subiectul activității"
                    hint={t('help.subject')}
                  />
                </div>

                {!isEditing && (
                  <Select
                    label={t('workPackages.fields.project')}
                    options={projectOptions}
                    value={formData.projectId || ''}
                    onChange={(e) =>
                      handleChange(
                        'projectId',
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    required
                    placeholder={t('common.selectOption')}
                  />
                )}

                {initialTypeId ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('workPackages.fields.type')}
                    </label>
                    <div className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700">
                      {selectedType?.name || t('common.loading')}
                    </div>
                  </div>
                ) : (
                  <Select
                    label={t('workPackages.fields.type')}
                    options={typeOptions}
                    value={formData.typeId || ''}
                    onChange={(e) =>
                      handleChange(
                        'typeId',
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    placeholder={t('common.selectOption')}
                  />
                )}

                <Select
                  label={t('workPackages.fields.status')}
                  options={statusOptions}
                  value={formData.statusId || ''}
                  onChange={(e) =>
                    handleChange(
                      'statusId',
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  placeholder={t('common.selectOption')}
                />

                <Select
                  label={t('workPackages.fields.priority')}
                  options={priorityOptions}
                  value={formData.priorityId || ''}
                  onChange={(e) =>
                    handleChange(
                      'priorityId',
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  placeholder={t('common.selectOption')}
                  hint={t('help.priority')}
                />

                <DateInput
                  label={t('workPackages.fields.startDate')}
                  value={formData.startDate || ''}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                />

                <DateInput
                  label={t('workPackages.fields.dueDate')}
                  value={formData.dueDate || ''}
                  onChange={(e) => handleChange('dueDate', e.target.value)}
                  hint={t('help.dueDate')}
                />

                <Select
                  label={t('workPackages.fields.assignee')}
                  options={assigneeOptions}
                  value={formData.assigneeId || ''}
                  onChange={(e) =>
                    handleChange(
                      'assigneeId',
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  placeholder={t('common.selectOption')}
                  hint={t('help.assignee')}
                />

                <Select
                  label={t('workPackages.fields.responsible')}
                  options={assigneeOptions}
                  value={formData.responsibleId || ''}
                  onChange={(e) =>
                    handleChange(
                      'responsibleId',
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  placeholder={t('common.selectOption')}
                />

                <Select
                  label={t('workPackages.fields.parent')}
                  options={parentOptions}
                  value={formData.parentId || ''}
                  onChange={(e) =>
                    handleChange(
                      'parentId',
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  placeholder={t('common.selectOption')}
                  hint="Selectați o activitate părinte (opțional)"
                />

                <div className="md:col-span-2">
                  <Textarea
                    label={t('workPackages.fields.description')}
                    value={formData.description || ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={6}
                    placeholder="Descrieți detaliat ce trebuie făcut..."
                    hint={t('help.description')}
                  />
                </div>
              </div>
            </TabPanel>

            {/* Tab: Planificare */}
            <TabPanel tabId="planning" activeTab={activeTab}>
              <div className="space-y-8">
                {/* Secțiune Date */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                    Perioada de execuție
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <DateInput
                      label={t('workPackages.fields.startDate')}
                      value={formData.startDate || ''}
                      onChange={(e) => handleChange('startDate', e.target.value)}
                    />

                    <DateInput
                      label={t('workPackages.fields.dueDate')}
                      value={formData.dueDate || ''}
                      onChange={(e) => handleChange('dueDate', e.target.value)}
                    />
                  </div>
                </div>

                {/* Secțiune Timp de lucru */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                    <Timer className="w-4 h-4 text-primary-500" />
                    Buget ore de lucru
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Ore estimate - editabil */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ore estimate (total)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min={0}
                          step={0.5}
                          value={estimatedHours || ''}
                          onChange={(e) => handleEstimatedHoursChange(Number(e.target.value) || 0)}
                          className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                          placeholder="0"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                          ore
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Numărul total de ore estimate pentru această activitate
                      </p>
                    </div>

                    {/* Ore înregistrate - readonly */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ore înregistrate
                      </label>
                      <div className="relative">
                        <div className="w-full px-3 py-2 pr-12 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700 font-medium">
                          {spentHours > 0 ? spentHours.toFixed(1) : '0'}
                        </div>
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                          ore
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Ore completate de utilizatori ({timeEntries.length} înregistrări)
                      </p>
                    </div>

                    {/* Ore rămase - editabil cu limitare */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ore rămase
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min={0}
                          max={availableRemainingHours}
                          step={0.5}
                          value={remainingHours || ''}
                          onChange={(e) => handleRemainingHoursChange(Number(e.target.value) || 0)}
                          className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                          placeholder="0"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                          ore
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Disponibil: max {formatHoursDisplay(availableRemainingHours)}
                      </p>
                    </div>
                  </div>

                  {/* Bară vizuală progres */}
                  {estimatedHours > 0 && (
                    <div className="mt-5 pt-4 border-t border-gray-200">
                      <div className="flex justify-between text-xs text-gray-500 mb-2">
                        <span>Progres buget timp</span>
                        <span>
                          {formatHoursDisplay(spentHours)} / {formatHoursDisplay(estimatedHours)}
                          ({Math.round((spentHours / estimatedHours) * 100)}%)
                        </span>
                      </div>
                      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full flex">
                          <div
                            className={`transition-all ${
                              spentHours > estimatedHours ? 'bg-red-500' :
                              spentHours > estimatedHours * 0.8 ? 'bg-orange-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min((spentHours / estimatedHours) * 100, 100)}%` }}
                          />
                          {remainingHours > 0 && spentHours < estimatedHours && (
                            <div
                              className="bg-blue-300 transition-all"
                              style={{ width: `${Math.min((remainingHours / estimatedHours) * 100, 100 - (spentHours / estimatedHours) * 100)}%` }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="flex gap-4 mt-2 text-xs">
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded bg-green-500"></span>
                          Înregistrat
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded bg-blue-300"></span>
                          Rămas planificat
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded bg-gray-200"></span>
                          Nealocate
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Avertizare depășire */}
                  {spentHours > estimatedHours && estimatedHours > 0 && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Buget depășit</p>
                        <p className="text-xs text-red-600">
                          S-au înregistrat {formatHoursDisplay(spentHours - estimatedHours)} peste bugetul estimat.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </TabPanel>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              leftIcon={<Save className="w-4 h-4" />}
              isLoading={isSaving}
            >
              {t('common.save')}
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
